import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/seo/SEOHead";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import {
  blogIndexPath,
  formatBlogDate,
  resolveMetaDescription,
} from "@/lib/blog/constants";
import { BLOG_HERO_GRADIENT } from "@/lib/blog/ui";
import { fetchPublishedBlogPosts } from "@/lib/blog/queries";
import { blogQueryKeys } from "@/lib/blog/query-keys";

const BlogIndexPage = () => {
  const { data: posts = [], isLoading, isError } = useQuery({
    queryKey: blogQueryKeys.publishedPosts,
    queryFn: fetchPublishedBlogPosts,
  });

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Insights — LaunchGuide.io"
        description="Practical growth marketing insights, launch strategies, and playbooks for product teams shipping their first product."
        pathname={blogIndexPath()}
      />
      <Navbar />

      <section className="relative border-b overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{ background: BLOG_HERO_GRADIENT }}
        />
        <div className="container max-w-5xl py-16 md:py-20">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <BookOpen className="h-4 w-4" aria-hidden />
            <span>LaunchGuide Insights</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-balance max-w-3xl">
            Growth marketing insights for builders
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl text-balance">
            Actionable articles on launch strategy, content, distribution, and the playbooks that help early-stage teams grow.
          </p>
        </div>
      </section>

      <section className="container max-w-5xl py-12 md:py-16">
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Card key={index} className="h-56 animate-pulse bg-muted/40 border-dashed" />
            ))}
          </div>
        ) : null}

        {isError ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">Unable to load articles right now. Please try again later.</p>
          </Card>
        ) : null}

        {!isLoading && !isError && posts.length === 0 ? (
          <Card className="p-10 md:p-12 text-center border-dashed">
            <BookOpen className="h-10 w-10 mx-auto text-muted-foreground mb-4" aria-hidden />
            <h2 className="text-xl font-semibold mb-2">Articles coming soon</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              New insights will appear here as they are published.
            </p>
          </Card>
        ) : null}

        {!isLoading && !isError && posts.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2">
            {posts.map((post) => {
              const description = resolveMetaDescription(post);
              const dateLabel = formatBlogDate(post.published_at || post.created_at);

              return (
                <Link
                  key={post.id}
                  to={`${blogIndexPath()}/${post.slug}`}
                  className="group block h-full"
                >
                  <Card className="h-full p-6 md:p-7 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-primary/20">
                    <div className="flex flex-col h-full">
                      {post.tags && post.tags.length > 0 ? (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.tags.slice(0, 3).map((tag) => (
                            <Badge key={tag} variant="secondary" className="font-normal">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      ) : null}

                      <h2 className="text-xl font-semibold leading-snug group-hover:text-primary transition-colors">
                        {post.title}
                      </h2>

                      {description ? (
                        <p className="mt-3 text-muted-foreground line-clamp-3 flex-1">
                          {description}
                        </p>
                      ) : null}

                      <div className="mt-6 flex items-center justify-between text-sm text-muted-foreground">
                        {dateLabel ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5" aria-hidden />
                            {dateLabel}
                          </span>
                        ) : (
                          <span />
                        )}
                        <span className="inline-flex items-center gap-1 font-medium text-primary">
                          Read article
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
                        </span>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        ) : null}
      </section>

      <Footer />
    </div>
  );
};

export default BlogIndexPage;
